import { FieldError, useForm } from 'react-hook-form';
import './DynamicFormPage.css';
import DynamicFormInput from '../DynamicFormInput/DynamicFormInput';
import DynamicFormDropdown from '../DynamicFormDropdown/DynamicFormDropdown';
import axios from 'axios';
import CustomToast from '../CustomToast/CustomToast';
import { useToast } from '@chakra-ui/react';
import { API_URL } from '../../constants';
import * as Sentry from '@sentry/react';
import DynamicFormFooter from '../DynamicFormFooter/DynamicFormFooter'
import DynamicDateInput from '../DynamicDateInput/DynamicDateInput';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import DynamicConsentRadioButton from '../DynamicConsentRadioButton/DynamicConsentRadioButton';
import { deriveTheme, themeToCssVars } from '../../theme/formTheme';

type FormField = {
  position: number;
  id: string;
  label: string;
  extId?: boolean;
  required: boolean;
  validation?: {
    name: string;
    pattern: string;
    message: string;
  };
  type: string;
  options?: {
    value: string;
    label: string;
  }[];
  placeholder?: string;
};

type Props = {
  attributes: {
    slug: string;
    logoImageUrl: string;
    faviconUrl: string;
    formFields: FormField[];
    clientWebsite: string;
    componentFillColor: string;
    componentFontColor: string;
    backgroundColor: string;
    buttonColor: string;
    buttonCtaText: string;
    buttonCtaColor: string;
    templateId: string;
    footerContent: string;
    footerColor: string;
    footerEnabled: boolean;
    dataProcessingConsentRequired: boolean;
    dataProcessingConsentText: string;
  };
  queryParams: any[]
};

const titleizeSlug = (str: string) => {
  return str.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const DynamicFormPage = ({ attributes, queryParams }: Props) => {
  const [consented, setConsented] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const hasAutoSubmittedRef = useRef(false);
  const consentRef = useRef<HTMLInputElement>(null);
  const {
    slug,
    logoImageUrl,
    faviconUrl,
    formFields,
    clientWebsite,
    componentFillColor,
    componentFontColor,
    buttonColor,
    buttonCtaText,
    buttonCtaColor,
    backgroundColor,
    footerContent,
    footerColor,
    footerEnabled,
    dataProcessingConsentRequired,
    dataProcessingConsentText,
  } = attributes;

  const extIdField = formFields.find((field) => field.extId)?.label;
  const toast = useToast();
  const { register, handleSubmit, formState: { errors, isSubmitting }, control } = useForm();

  const theme = useMemo(
    () => deriveTheme({ backgroundColor, componentFillColor, componentFontColor, buttonColor, buttonCtaColor, footerColor }),
    [backgroundColor, componentFillColor, componentFontColor, buttonColor, buttonCtaColor, footerColor]
  );

  const consentMissing = dataProcessingConsentRequired && !consented;

  const pageTitle = titleizeSlug(slug);
  document.title = pageTitle;

  if (faviconUrl) {
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) {
      favicon.href = faviconUrl;
    }
  }

  document.body.style.color = theme.text;
  document.body.style.backgroundColor = theme.page;

  const renderToast = (title: string, description: string = "Please try again later") => {
    const id = `error-toast-${title}`;
    if (toast.isActive(id)) return;
    toast({
      render: (props) => (
        <CustomToast
          status="error"
          title={title}
          description={description}
          onClose={props.onClose}
        />
      ),
      duration: 5000,
      isClosable: true,
      id,
    });
  };

  const onSubmit = async (data: any) => {
    const passQueryParams = queryParams.filter((param) => !param.groupTag || param.groupTags).map((param) => {
      const key = Object.keys(param)[0];
      return [key, param[key] || []];
    });

    data.groupTags = queryParams.reduce((acc: string[], param) => {
      if (param.groupTag) {
        acc.push(param.groupTag);
      }
      if (param.groupTags) {
        acc.push(...param.groupTags);
      }
      return acc;
    }, []);

    let formData = formFields.map(field => {
      const fieldValue = data[field.id];
      if (field.required || (fieldValue !== undefined && fieldValue !== null && fieldValue !== '')) {
        return [field.id, fieldValue];
      }
      return null;
    }).filter(entry => entry !== null);

    passQueryParams.forEach(([key, value]) => {
      if (!formData.some(entry => entry && entry[0] === key)) {
        formData.push([key, value]);
      }
    });

    const groupTags = [...data.groupTags, ...formFields.map(field => {
      if (field.id === "none" && data[`${field.id}-${field.position}`]) {
        return data[`${field.id}-${field.position}`];
      }
    }).filter(entry => entry !== undefined)];

    const formDataEntries = [...formData, ["groupTags", Array.from(new Set(groupTags))]];

    try {
      const response = await axios.post(
        `${API_URL}/internal/v1/dynamic_form_templates/${slug}/passes`,
        {
          pass: Object.fromEntries(formDataEntries),
          ...(dataProcessingConsentRequired && {
            consent_meta: {
              data_processing: {
                consented: consented,
                consented_at: consented ? new Date().toISOString() : null,
              }
            }
          }),
        },
        {
          headers: {
            "Content-Type": "application/json",
          }
        }
      );

      const { data: { attributes: { downloadUrl } } } = response.data;

      if (response.status === 200 || response.status === 201) {
        window.location.href = downloadUrl;
      }
    } catch (error: any) {
      if (error.response.status === 409) {
        renderToast(`A pass with this ${extIdField} already exists.`, "");
      } else if (error.response.status === 402 || error.response.status === 400) {
        error.response.data.errors.forEach((error: string) => {
          renderToast(error.charAt(0).toUpperCase() + error.slice(1), "");
        });
      } else {
        renderToast("Something went wrong, please try again");
        Sentry.captureException(error);
      }
    }
  };

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    setShowConsentError(consentMissing);
    return handleSubmit(consentMissing ? () => consentRef.current?.focus() : onSubmit)(event);
  };

  const handleConsentChange = (value: boolean) => {
    setConsented(value);
    if (value) setShowConsentError(false);
  };

  useEffect(() => {
    if (formFields.length === 0 && !hasAutoSubmittedRef.current) {
      hasAutoSubmittedRef.current = true;
      onSubmit({});
    }
  }, []);

  return (
    <>
      {formFields.length > 0 && (
        <div className='form-page' style={themeToCssVars(theme)}>
          <form className='form-card' onSubmit={onFormSubmit} noValidate aria-labelledby='form-title'>
            <h1 id='form-title' className='visually-hidden'>{pageTitle}</h1>
            {logoImageUrl && (
              <a className='form-logo' href={clientWebsite} aria-label={clientWebsite ? 'Visit website' : undefined}>
                <img src={logoImageUrl} alt="" />
              </a>
            )}
            <div className='form-fields'>
              {formFields.sort((a, b) => a.position - b.position).map((field) => (
                field.type === "dropdown" ? (
                  <DynamicFormDropdown
                    key={field.position}
                    field={field}
                    error={errors[field.id === "none" ? `${field.id}-${field.position}` : field.id] as FieldError}
                    control={control}
                  />
                ) : field.type === "date" ? (
                  <DynamicDateInput
                    key={field.position}
                    field={field}
                    register={register}
                    error={errors[field.id] as FieldError}
                  />
                ) : (
                  <DynamicFormInput
                    key={field.position}
                    field={field}
                    register={register}
                    error={errors[field.id] as FieldError}
                    value={queryParams.find((param) => param[field.id])?.[field.id] || ''}
                  />
                )
              ))}
            </div>
            {dataProcessingConsentText && (
              <DynamicConsentRadioButton
                consented={consented}
                setConsented={handleConsentChange}
                dataProcessingConsentText={dataProcessingConsentText}
                consentRequired={dataProcessingConsentRequired}
                showError={showConsentError}
                checkboxRef={consentRef}
              />
            )}
            <button className='submit-button' type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
              {isSubmitting && <span className='submit-spinner' aria-hidden="true" />}
              <span>{isSubmitting ? 'Submitting' : buttonCtaText}</span>
            </button>
            <div className='visually-hidden' aria-live='polite'>{isSubmitting ? 'Submitting' : ''}</div>
          </form>
          {footerEnabled && (
            <footer className='form-footer'>
              <DynamicFormFooter content={footerContent} />
            </footer>
          )}
        </div>
      )}
    </>
  );
};

export default DynamicFormPage;
